import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import { DEFAULT_NOT_SELECTED_REASONS } from '../constants';

export type NotSelectedReasonRow = { label: string; count: number };

type Props = {
  reasonRows: NotSelectedReasonRow[];
  total: number;
};

const ROW_HEIGHT = 32;

// Height (px) reserved by MetricDetailLayout's loading/empty placeholder — sized off the default
// reason set plus the synthetic "chose different facility" bucket, so the card reserves the expected
// row count instead of a magic number. A client with a custom (longer/shorter) reason list still
// shifts once on first open, but the common default case lands where the placeholder reserved.
export const NOT_SELECTED_CHART_HEIGHT = (DEFAULT_NOT_SELECTED_REASONS.length + 1) * ROW_HEIGHT;

// The Not Selected drill's "Not Selected Reason" chart (OLIO-2899): horizontal ranked amber bars
// over a grey track, one per reason (most → least), with a right-hand "N (pct%)" column. Modeled on
// the outbound DenialRateDetailHeader bar list; kept local to the inbound folder like the denial
// donut. Rows arrive pre-sorted from the side query.
export default function InboundNotSelectedBreakdownChart({ reasonRows, total }: Props) {
  const chartOptions = useMemo<Highcharts.Options>(() => {
    const maxCount = reasonRows.reduce((m, r) => Math.max(m, r.count), 0);

    return {
      chart: {
        type: 'bar',
        style: { fontFamily: textStyles.base.fontFamily },
        height: reasonRows.length * ROW_HEIGHT,
        spacingTop: 0,
        spacingBottom: 0,
        marginRight: 24,
      },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: { enabled: false },
      xAxis: {
        categories: reasonRows.map((r) => r.label),
        lineColor: colors.black10,
        tickLength: 0,
        // Left-align the reason labels in the gutter (default bar-chart labels hug the axis /
        // right-align); reserveSpace keeps them from overlapping the bars.
        labels: { align: 'left', reserveSpace: true, style: { fontSize: '12px', color: colors.black75 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineWidth: 0,
        lineWidth: 0,
        labels: { enabled: false },
        tickLength: 0,
        max: maxCount || undefined,
        opposite: true,
      },
      tooltip: { enabled: false },
      plotOptions: {
        series: { grouping: false, animation: false, groupPadding: 0, pointPadding: 0 },
        bar: {
          pointWidth: 10,
          borderRadius: { radius: 3, scope: 'point', where: 'end' },
          dataLabels: { enabled: false },
        },
      },
      series: [
        {
          type: 'bar',
          name: 'Track',
          data: reasonRows.map(() => maxCount),
          color: colors.black5,
          enableMouseTracking: false,
          showInLegend: false,
        },
        {
          type: 'bar',
          name: 'Not Selected',
          data: reasonRows.map((r) => ({ y: r.count, color: colors.yellow100 })),
        },
      ],
    };
  }, [reasonRows]);

  return (
    <ChartRow>
      <ChartFill>
        <HighchartsReact highcharts={Highcharts} options={chartOptions} />
      </ChartFill>
      <NumberColumn $rows={reasonRows.length} $rowHeight={ROW_HEIGHT}>
        {reasonRows.map((r, i) => {
          const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
          return (
            <NumberCell key={i}>
              {r.count} <Pct>({pct}%)</Pct>
            </NumberCell>
          );
        })}
      </NumberColumn>
    </ChartRow>
  );
}

const ChartRow = styled.div`
  display: flex;
  align-items: stretch;
`;

const ChartFill = styled.div`
  flex: 1;
  min-width: 0;
`;

const NumberColumn = styled.div<{ $rows: number; $rowHeight: number }>`
  display: grid;
  grid-template-rows: ${({ $rows, $rowHeight }) => `repeat(${$rows}, ${$rowHeight}px)`};
  width: 68px;
`;

const NumberCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  font-size: 12px;
  font-weight: var(--font-weight-medium);
  color: ${({ theme }) => theme.colors.black100};
`;

const Pct = styled.span`
  font-weight: var(--font-weight-regular);
  color: ${({ theme }) => theme.colors.black50};
`;
